import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";
import { Heart, Zap, Shield, Flame, Skull, Wind, Eye } from "lucide-react";

// Dragon attack effects (Level 100)
const DRAGON_ATTACK_EFFECTS = {
  fire_breath: {
    name: "Fire Breath",
    emoji: "🔥",
    description: "Burns 3 random cells!",
    cellsAffected: 3,
    color: "from-orange-500 to-red-600"
  },
  lava_rain: {
    name: "Lava Rain", 
    emoji: "🌋",
    description: "A row of lava falls!",
    cellsAffected: 8, // Full row
    color: "from-red-600 to-yellow-500"
  },
  inferno: {
    name: "Inferno",
    emoji: "💀",
    description: "5 cells burn + board shuffles!",
    cellsAffected: 5,
    shuffle: true,
    color: "from-purple-600 to-red-600"
  }
};

// Owl Guardian attack effects (Level 140)
const OWL_ATTACK_EFFECTS = {
  wind_gust: {
    name: "Wind Gust",
    emoji: "💨",
    description: "Shuffles a row of cells!",
    cellsAffected: 8,
    shuffle: true,
    color: "from-slate-500 to-cyan-600"
  },
  shadow_veil: {
    name: "Shadow Veil",
    emoji: "🌑",
    description: "Covers 4 cells in darkness!",
    cellsAffected: 4,
    color: "from-gray-800 to-purple-900"
  },
  piercing_gaze: {
    name: "Piercing Gaze",
    emoji: "👁️",
    description: "Freezes 3 cells in place!",
    cellsAffected: 3,
    freeze: true,
    color: "from-yellow-500 to-amber-600"
  },
  night_terror: {
    name: "Night Terror",
    emoji: "🦇",
    description: "5 cells transform + vines spawn!",
    cellsAffected: 5,
    spawnVines: true,
    color: "from-purple-900 to-indigo-900"
  }
};

// Giant Kraken attack effects (Level 160)
const KRAKEN_ATTACK_EFFECTS = {
  whirlpool: {
    name: "Whirlpool",
    emoji: "🌀",
    description: "Swirls and shuffles 6 cells!",
    cellsAffected: 6,
    shuffle: true,
    color: "from-blue-600 to-cyan-500"
  },
  ink_blast: {
    name: "Ink Blast",
    emoji: "🖤",
    description: "Covers 4 cells in ink!",
    cellsAffected: 4,
    color: "from-slate-900 to-purple-900"
  },
  tentacle_slam: {
    name: "Tentacle Slam",
    emoji: "🐙",
    description: "Smashes 5 random cells!",
    cellsAffected: 5,
    color: "from-purple-700 to-pink-600"
  },
  tidal_wave: {
    name: "Tidal Wave",
    emoji: "🌊",
    description: "A massive wave hits the board!",
    cellsAffected: 8,
    color: "from-blue-500 to-teal-400"
  }
};

// Phoenix attack effects (Volcanic Core - Level 210, 220)
const PHOENIX_ATTACK_EFFECTS = {
  flame_wave: {
    name: "Flame Wave",
    emoji: "🔥",
    description: "A wave of fire sweeps the board!",
    cellsAffected: 6,
    color: "from-orange-500 to-yellow-400"
  },
  rebirth_flame: {
    name: "Rebirth Flame",
    emoji: "🦅",
    description: "Phoenix rises with healing fire!",
    cellsAffected: 4,
    color: "from-yellow-400 to-red-500"
  },
  ash_storm: {
    name: "Ash Storm",
    emoji: "🌫️",
    description: "Ash covers 5 cells!",
    cellsAffected: 5,
    color: "from-gray-600 to-orange-600"
  }
};

// Dream Weaver attack effects (Moonlight Garden - Level 230, 240)
const DREAM_WEAVER_ATTACK_EFFECTS = {
  illusion_mist: {
    name: "Illusion Mist",
    emoji: "💫",
    description: "Creates illusion fruits!",
    cellsAffected: 4,
    color: "from-purple-500 to-pink-500"
  },
  eternal_night: {
    name: "Eternal Night",
    emoji: "🌑",
    description: "Darkness engulfs 6 cells!",
    cellsAffected: 6,
    color: "from-indigo-900 to-purple-900"
  },
  dream_trap: {
    name: "Dream Trap",
    emoji: "😴",
    description: "Sleeps 4 cells for 2 turns!",
    cellsAffected: 4,
    freeze: true,
    color: "from-blue-400 to-purple-500"
  }
};

// Alchemist attack effects (Alchemist's Lab - Level 250, 260)
const ALCHEMIST_ATTACK_EFFECTS = {
  toxic_brew: {
    name: "Toxic Brew",
    emoji: "🧪",
    description: "Poison spreads to 5 cells!",
    cellsAffected: 5,
    color: "from-green-600 to-lime-500"
  },
  transmute: {
    name: "Transmute",
    emoji: "⚗️",
    description: "Transforms random fruits!",
    cellsAffected: 4,
    shuffle: true,
    color: "from-emerald-500 to-teal-400"
  },
  explosion: {
    name: "Lab Explosion",
    emoji: "💥",
    description: "Volatile reaction!",
    cellsAffected: 7,
    color: "from-yellow-500 to-red-600"
  }
};

// Circus attack effects (Circus Carnival - Level 270, 280)
const CIRCUS_ATTACK_EFFECTS = {
  juggle_bombs: {
    name: "Juggle Bombs",
    emoji: "🎪",
    description: "Bombs bounce around!",
    cellsAffected: 5,
    color: "from-pink-500 to-purple-500"
  },
  cannon_shot: {
    name: "Cannon Shot",
    emoji: "💣",
    description: "Cannonball clears a line!",
    cellsAffected: 8,
    color: "from-gray-700 to-red-600"
  },
  confetti_blast: {
    name: "Confetti Blast",
    emoji: "🎉",
    description: "Colorful chaos!",
    cellsAffected: 6,
    shuffle: true,
    color: "from-yellow-400 to-pink-500"
  }
};

// Pirate attack effects (Pirate Shipwreck - Level 290, 300)
const PIRATE_ATTACK_EFFECTS = {
  cannon_barrage: {
    name: "Cannon Barrage",
    emoji: "💣",
    description: "Cannons fire!",
    cellsAffected: 6,
    color: "from-gray-600 to-orange-500"
  },
  ghost_crew: {
    name: "Ghost Crew",
    emoji: "👻",
    description: "Ghosts haunt 4 cells!",
    cellsAffected: 4,
    color: "from-teal-600 to-gray-500"
  },
  kraken_call: {
    name: "Kraken Call",
    emoji: "🐙",
    description: "Tentacles grab 5 cells!",
    cellsAffected: 5,
    color: "from-purple-700 to-blue-600"
  }
};

// Temple attack effects (Cherry Blossom Temple - Level 310, 320)
const TEMPLE_ATTACK_EFFECTS = {
  cherry_storm: {
    name: "Cherry Storm",
    emoji: "🌸",
    description: "Petals cover 5 cells!",
    cellsAffected: 5,
    color: "from-pink-400 to-rose-500"
  },
  spirit_strike: {
    name: "Spirit Strike",
    emoji: "👹",
    description: "Spirits attack 4 cells!",
    cellsAffected: 4,
    color: "from-red-600 to-purple-600"
  },
  zen_meditation: {
    name: "Zen Meditation",
    emoji: "🧘",
    description: "Time freezes briefly!",
    cellsAffected: 0,
    freeze: true,
    color: "from-blue-300 to-teal-300"
  }
};

// Storm attack effects (Storm Kingdom - Level 330, 340)
const STORM_ATTACK_EFFECTS = {
  lightning_storm: {
    name: "Lightning Storm",
    emoji: "⚡",
    description: "Multiple strikes!",
    cellsAffected: 6,
    color: "from-yellow-400 to-blue-500"
  },
  thunder_clap: {
    name: "Thunder Clap",
    emoji: "🌩️",
    description: "Shockwave hits 5 cells!",
    cellsAffected: 5,
    color: "from-purple-500 to-yellow-500"
  },
  tornado: {
    name: "Tornado",
    emoji: "🌪️",
    description: "Board shuffles!",
    cellsAffected: 8,
    shuffle: true,
    color: "from-gray-500 to-blue-600"
  }
};

// Mushroom attack effects (Mushroom Cavern - Level 350, 360)
const MUSHROOM_ATTACK_EFFECTS = {
  spore_cloud: {
    name: "Spore Cloud",
    emoji: "🍄",
    description: "Spores spread to 5 cells!",
    cellsAffected: 5,
    color: "from-purple-600 to-pink-500"
  },
  toxic_gas: {
    name: "Toxic Gas",
    emoji: "💨",
    description: "Poison gas fills area!",
    cellsAffected: 6,
    color: "from-green-700 to-purple-600"
  },
  mycelium_trap: {
    name: "Mycelium Trap",
    emoji: "🕸️",
    description: "Roots trap 4 cells!",
    cellsAffected: 4,
    freeze: true,
    color: "from-brown-500 to-gray-600"
  }
};

// Casino attack effects (Casino Royale - Level 370, 380)
const CASINO_ATTACK_EFFECTS = {
  wild_cards: {
    name: "Wild Cards",
    emoji: "🃏",
    description: "Cards transform cells!",
    cellsAffected: 5,
    shuffle: true,
    color: "from-red-500 to-black"
  },
  dice_roll: {
    name: "Dice Roll",
    emoji: "🎲",
    description: "Random effect!",
    cellsAffected: 4,
    color: "from-white to-gray-400"
  },
  jackpot_spin: {
    name: "Jackpot Spin",
    emoji: "🎰",
    description: "Slot machine chaos!",
    cellsAffected: 7,
    color: "from-yellow-500 to-red-500"
  }
};

// Cosmic attack effects (Cosmic Nebula - Level 390, 400)
const COSMIC_ATTACK_EFFECTS = {
  void_rift: {
    name: "Void Rift",
    emoji: "🕳️",
    description: "Black hole pulls 5 cells!",
    cellsAffected: 5,
    color: "from-purple-900 to-black"
  },
  cosmic_annihilation: {
    name: "Cosmic Annihilation",
    emoji: "💫",
    description: "Reality bends!",
    cellsAffected: 8,
    shuffle: true,
    color: "from-purple-500 to-pink-500"
  },
  gravity_crush: {
    name: "Gravity Crush",
    emoji: "🌌",
    description: "Gravity intensifies!",
    cellsAffected: 6,
    color: "from-indigo-600 to-violet-600"
  }
};

// Get attack effects based on boss type
const getAttackEffects = (bossType) => {
  if (bossType === 'owl') return OWL_ATTACK_EFFECTS;
  if (bossType === 'kraken') return KRAKEN_ATTACK_EFFECTS;
  if (bossType === 'phoenix' || bossType === 'phoenix_final') return PHOENIX_ATTACK_EFFECTS;
  if (bossType === 'dream_weaver' || bossType === 'twilight') return DREAM_WEAVER_ATTACK_EFFECTS;
  if (bossType === 'alchemist' || bossType === 'transmuter') return ALCHEMIST_ATTACK_EFFECTS;
  if (bossType === 'ringmaster' || bossType === 'circus_king') return CIRCUS_ATTACK_EFFECTS;
  if (bossType === 'blackfin' || bossType === 'kraken_wrath') return PIRATE_ATTACK_EFFECTS;
  if (bossType === 'temple_guardian' || bossType === 'zen_master') return TEMPLE_ATTACK_EFFECTS;
  if (bossType === 'thunder_lord' || bossType === 'storm_king') return STORM_ATTACK_EFFECTS;
  if (bossType === 'spore_lord' || bossType === 'fungal_king') return MUSHROOM_ATTACK_EFFECTS;
  if (bossType === 'dealer' || bossType === 'jackpot_king') return CASINO_ATTACK_EFFECTS;
  if (bossType === 'void_walker' || bossType === 'celestial_dragon') return COSMIC_ATTACK_EFFECTS;
  return DRAGON_ATTACK_EFFECTS;
};

// Keep backward compatibility
const ATTACK_EFFECTS = DRAGON_ATTACK_EFFECTS;

// Boss HP Bar Component
export function BossHPBar({ currentHP, maxHP, phase, totalPhases, bossName, isAttacking, weakSpot }) {
  const hpPercentage = Math.max(0, (currentHP / maxHP) * 100);
  
  return (
    <div className="w-full bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-3 border-2 border-red-500/50 shadow-lg">
      {/* Boss Name & Phase */}
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          <Skull className={cn(
            "w-5 h-5 text-red-500",
            isAttacking && "animate-pulse"
          )} />
          <span className="font-display font-bold text-white text-lg">{bossName}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Phase</span>
          <div className="flex gap-1">
            {[...Array(totalPhases)].map((_, i) => (
              <div 
                key={i}
                className={cn(
                  "w-3 h-3 rounded-full border-2",
                  i < phase ? "bg-red-500 border-red-400" : 
                  i === phase ? "bg-yellow-500 border-yellow-400 animate-pulse" : 
                  "bg-gray-700 border-gray-600"
                )}
              />
            ))}
          </div>
        </div>
      </div>
      
      {/* HP Bar */}
      <div className="relative h-6 bg-gray-700 rounded-full overflow-hidden border border-gray-600">
        {/* HP Fill */}
        <div 
          className={cn(
            "absolute inset-y-0 left-0 transition-all duration-500 rounded-full",
            hpPercentage > 60 ? "bg-gradient-to-r from-green-600 to-green-400" :
            hpPercentage > 30 ? "bg-gradient-to-r from-yellow-600 to-yellow-400" :
            "bg-gradient-to-r from-red-600 to-red-400",
            isAttacking && "animate-pulse"
          )}
          style={{ width: `${hpPercentage}%` }}
        >
          {/* Shine effect */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent h-1/2" />
        </div>
        
        {/* HP Text */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-bold text-white text-sm drop-shadow-lg">
            {Math.ceil(currentHP)} / {maxHP}
          </span>
        </div>
      </div>
      
      {/* Weak Spot Indicator */}
      <div className="flex items-center justify-center gap-2 mt-2 text-sm">
        <Shield className="w-4 h-4 text-yellow-400" />
        <span className="text-gray-300">Weak Spot:</span>
        <span className="text-2xl animate-bounce">{weakSpot}</span>
        <span className="text-yellow-400 font-bold">(2.5x damage!)</span>
      </div>
    </div>
  );
}

// Dragon Visual Component - 3D Animated with Enhanced Effects
export function DragonVisual({ 
  image, 
  isAttacking, 
  currentPhase, 
  isDamaged,
  attackType 
}) {
  const [showDamage, setShowDamage] = useState(false);
  const breathRef = useRef(0);
  const wingRef = useRef(0);
  const [, forceUpdate] = useState(0);
  
  useEffect(() => {
    if (isDamaged) {
      setShowDamage(true);
      const timer = setTimeout(() => setShowDamage(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isDamaged]);

  // Single animation frame loop instead of 2 separate high-freq intervals
  useEffect(() => {
    let raf;
    let last = 0;
    const tick = (ts) => {
      if (ts - last > 80) { // ~12fps for ambient animation (was 20-33fps)
        breathRef.current = (breathRef.current + 1) % 100;
        wingRef.current = (wingRef.current + 3) % 360;
        forceUpdate(v => v + 1);
        last = ts;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Memoize particle positions
  const ambientPositions = useMemo(() => Array.from({ length: 10 }, (_, i) => ({
    left: `${5 + i * 9.5}%`, delay: `${i * 0.4}s`
  })), []);
  const dustPositions = useMemo(() => Array.from({ length: 8 }, (_, i) => ({
    left: `${12 + i * 11}%`, top: `${15 + (i * 13) % 70}%`, delay: `${i * 0.5}s`, duration: `${3 + (i % 3)}s`
  })), []);
  const auraPositions = useMemo(() => Array.from({ length: 6 }, (_, i) => ({
    delay: `${i * 0.6}s`, duration: `${3 + (i % 3)}s`
  })), []);

  const breathScale = 1 + Math.sin(breathRef.current * 0.1) * 0.03;
  const wingRotate = Math.sin(wingRef.current * 0.05) * 3;
  
  // Phase-based colors
  const phaseColors = {
    0: { primary: 'orange', glow: 'rgba(255, 165, 0, 0.7)', fire: '#ff6b00', secondary: '#ffaa00' },
    1: { primary: 'red', glow: 'rgba(255, 50, 50, 0.7)', fire: '#ff0000', secondary: '#ff6600' },
    2: { primary: 'purple', glow: 'rgba(150, 50, 255, 0.7)', fire: '#9932cc', secondary: '#ff00ff' }
  };
  const colors = phaseColors[currentPhase] || phaseColors[0];

  return (
    <div className="relative w-full flex justify-center mb-2 perspective-1000">
      {/* Background energy waves */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64">
          <div className="absolute inset-0 rounded-full animate-energy-wave" style={{ borderColor: colors.fire, opacity: 0.3 }} />
          <div className="absolute inset-0 rounded-full animate-energy-wave" style={{ borderColor: colors.secondary, animationDelay: '0.5s', opacity: 0.2 }} />
          <div className="absolute inset-0 rounded-full animate-energy-wave" style={{ borderColor: colors.fire, animationDelay: '1s', opacity: 0.15 }} />
        </div>
      </div>

      {/* Ambient fire particles - reduced */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {ambientPositions.map((pos, i) => (
          <div
            key={`ambient-${i}`}
            className="absolute animate-ambient-fire"
            style={{ left: pos.left, bottom: '15%', animationDelay: pos.delay, opacity: 0.7 }}
          >
            <span className="text-lg" style={{ color: colors.fire, filter: `drop-shadow(0 0 5px ${colors.fire})` }}>✦</span>
          </div>
        ))}
      </div>

      {/* Magical dust particles - reduced */}
      <div className="absolute inset-0 pointer-events-none">
        {dustPositions.map((pos, i) => (
          <div
            key={`dust-${i}`}
            className="absolute animate-magic-dust"
            style={{ left: pos.left, top: pos.top, animationDelay: pos.delay, animationDuration: pos.duration }}
          >
            <span className="text-xs" style={{ color: colors.secondary }}>✨</span>
          </div>
        ))}
      </div>

      {/* Main Dragon Container with 3D effects */}
      <div 
        className={cn(
          "relative w-48 h-48 sm:w-56 sm:h-56 transition-all duration-300",
          isAttacking && "animate-dragon-attack-3d",
          showDamage && "animate-damage-shake"
        )}
        style={{
          transform: `scale(${breathScale}) rotate(${wingRotate}deg) ${isAttacking ? 'translateZ(30px)' : 'translateZ(0)'}`,
          transformStyle: 'preserve-3d'
        }}
      >
        {/* Multi-layer glow effect for depth - enhanced */}
        <div 
          className="absolute inset-[-20%] rounded-full blur-3xl animate-glow-pulse"
          style={{ 
            background: `radial-gradient(circle, ${colors.glow} 0%, transparent 60%)`,
            transform: 'translateZ(-20px) scale(1.6)'
          }} 
        />
        <div 
          className="absolute inset-[-10%] rounded-full blur-2xl animate-glow-pulse-delayed"
          style={{ 
            background: `radial-gradient(circle, ${colors.secondary}66 0%, transparent 50%)`,
            transform: 'translateZ(-10px) scale(1.3)'
          }} 
        />
        
        {/* Dragon shadow on ground - animated */}
        <div 
          className="absolute bottom-[-25px] left-1/2 w-40 h-10 rounded-full animate-shadow-pulse"
          style={{
            background: 'radial-gradient(ellipse, rgba(0,0,0,0.5) 0%, transparent 70%)',
            transform: `translateX(-50%) scaleX(${1 + Math.sin(breathRef.current * 0.1) * 0.1})`,
            filter: 'blur(5px)'
          }}
        />
        
        {/* Floating Dragon Image with enhanced animations */}
        <div className="relative w-full h-full animate-dragon-float">
          {/* Wing glow effects - left */}
          <div 
            className="absolute top-[30%] left-[-10%] w-16 h-16 rounded-full blur-xl animate-wing-glow"
            style={{ background: colors.glow }}
          />
          {/* Wing glow effects - right */}
          <div 
            className="absolute top-[30%] right-[-10%] w-16 h-16 rounded-full blur-xl animate-wing-glow"
            style={{ background: colors.glow, animationDelay: '0.5s' }}
          />
          
          <img 
            src={image} 
            alt="Fruit Dragon"
            className={cn(
              "w-full h-full object-contain transition-all duration-200",
              "animate-dragon-breathe",
              isAttacking && "brightness-125 saturate-150"
            )}
            style={{
              filter: `drop-shadow(0 0 ${isAttacking ? '40px' : '25px'} ${colors.fire}) drop-shadow(0 0 ${isAttacking ? '60px' : '35px'} ${colors.secondary}55)`
            }}
          />
          
          {/* Eye glow overlay - enhanced */}
          <div 
            className="absolute top-[28%] left-[38%] w-4 h-4 rounded-full animate-eye-glow"
            style={{
              background: `radial-gradient(circle, #fff 0%, ${colors.fire} 40%, transparent 100%)`,
              boxShadow: `0 0 15px ${colors.fire}, 0 0 25px ${colors.fire}, 0 0 35px ${colors.secondary}`
            }}
          />
          <div 
            className="absolute top-[28%] right-[38%] w-4 h-4 rounded-full animate-eye-glow"
            style={{
              background: `radial-gradient(circle, #fff 0%, ${colors.fire} 40%, transparent 100%)`,
              boxShadow: `0 0 15px ${colors.fire}, 0 0 25px ${colors.fire}, 0 0 35px ${colors.secondary}`,
              animationDelay: '0.15s'
            }}
          />
        </div>
        
        {/* Fire breath effect - enhanced with multiple layers */}
        <div className={cn(
          "absolute bottom-[25%] left-1/2 -translate-x-1/2 transition-all duration-300",
          isAttacking ? "opacity-100 scale-150" : "opacity-80 scale-100"
        )}>
          <div className="relative">
            {/* Main fire */}
            <div className="absolute animate-fire-flicker" style={{ animationDelay: '0s' }}>
              <span className="text-4xl" style={{ filter: `drop-shadow(0 0 15px ${colors.fire}) drop-shadow(0 0 25px ${colors.secondary})` }}>🔥</span>
            </div>
            <div className="absolute animate-fire-flicker" style={{ animationDelay: '0.1s', left: '-20px', top: '5px' }}>
              <span className="text-3xl" style={{ filter: `drop-shadow(0 0 12px ${colors.fire})` }}>🔥</span>
            </div>
            <div className="absolute animate-fire-flicker" style={{ animationDelay: '0.2s', left: '20px', top: '5px' }}>
              <span className="text-3xl" style={{ filter: `drop-shadow(0 0 12px ${colors.fire})` }}>🔥</span>
            </div>
            {/* Extra fire particles */}
            <div className="absolute animate-fire-flicker" style={{ animationDelay: '0.15s', left: '-35px', top: '10px' }}>
              <span className="text-2xl" style={{ filter: `drop-shadow(0 0 8px ${colors.fire})` }}>🔥</span>
            </div>
            <div className="absolute animate-fire-flicker" style={{ animationDelay: '0.25s', left: '35px', top: '10px' }}>
              <span className="text-2xl" style={{ filter: `drop-shadow(0 0 8px ${colors.fire})` }}>🔥</span>
            </div>
          </div>
        </div>

        {/* Tail swaying effect - sparkle trail */}
        <div className="absolute bottom-[15%] left-1/2 -translate-x-1/2">
          {[...Array(5)].map((_, i) => (
            <div
              key={`tail-${i}`}
              className="absolute animate-tail-sway"
              style={{
                left: `${-20 + i * 10}px`,
                animationDelay: `${i * 0.1}s`
              }}
            >
              <span className="text-sm" style={{ color: colors.secondary, filter: `drop-shadow(0 0 5px ${colors.secondary})` }}>✦</span>
            </div>
          ))}
        </div>

        {/* Attack Effect Overlay - enhanced */}
        {isAttacking && attackType && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <div className="relative">
              <span className="text-7xl animate-attack-burst" style={{ filter: `drop-shadow(0 0 40px ${colors.fire}) drop-shadow(0 0 60px ${colors.secondary})` }}>
                {ATTACK_EFFECTS[attackType]?.emoji || "🔥"}
              </span>
              {/* Multiple shockwave rings */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-24 h-24 rounded-full border-4 animate-shockwave" style={{ borderColor: colors.fire }} />
                <div className="w-24 h-24 rounded-full border-4 animate-shockwave" style={{ borderColor: colors.secondary, animationDelay: '0.15s' }} />
                <div className="w-24 h-24 rounded-full border-4 animate-shockwave" style={{ borderColor: colors.fire, animationDelay: '0.3s' }} />
              </div>
            </div>
          </div>
        )}
        
        {/* Damage Numbers - enhanced */}
        {showDamage && (
          <div className="absolute top-0 right-0 text-4xl font-bold text-yellow-400 animate-damage-pop z-30" style={{ filter: 'drop-shadow(0 0 10px #ff0)' }}>
            💥
          </div>
        )}
        
        {/* Particle aura around dragon - reduced */}
        <div className="absolute inset-0 pointer-events-none">
          {auraPositions.map((pos, i) => (
            <div
              key={`aura-${i}`}
              className="absolute animate-orbit-particle"
              style={{
                top: '50%',
                left: '50%',
                animationDelay: pos.delay,
                animationDuration: pos.duration
              }}
            >
              <span className="text-base" style={{ color: i % 2 === 0 ? colors.fire : colors.secondary, filter: `drop-shadow(0 0 8px ${colors.fire})` }}>✨</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Fire particles during attack - reduced */}
      {isAttacking && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-fire-burst"
              style={{
                left: `${8 + i * 7}%`,
                top: `${25 + (i * 5) % 40}%`,
                animationDelay: `${i * 0.06}s`
              }}
            >
              <span className="text-3xl" style={{ filter: `drop-shadow(0 0 15px ${colors.fire})` }}>🔥</span>
            </div>
          ))}
        </div>
      )}
      
      {/* Heat distortion overlay */}
      <div 
        className="absolute inset-0 pointer-events-none animate-heat-distort rounded-full"
        style={{
          background: `radial-gradient(circle, ${colors.fire}11 0%, transparent 70%)`,
          mixBlendMode: 'screen'
        }}
      />
    </div>
  );
}

// Owl Guardian Visual Component (Level 140 Boss)
export function OwlGuardianVisual({ currentPhase = 0, isAttacking = false, attackType = null, isDamaged = false }) {
  const [breathPhase, setBreathPhase] = useState(0);
  const [eyeGlow, setEyeGlow] = useState(false);
  const [showDamage, setShowDamage] = useState(false);

  // Memoize random positions
  const wispPositions = useMemo(() => Array.from({ length: 6 }, (_, i) => ({
    left: `${10 + i * 15}%`, top: `${30 + (i % 3) * 20}%`, delay: `${i * 1}s`
  })), []);
  const fireflyPositions = useMemo(() => Array.from({ length: 8 }, (_, i) => ({
    left: `${8 + i * 12}%`, bottom: `${5 + (i * 7) % 50}%`, delay: `${i * 0.5}s`
  })), []);

  useEffect(() => {
    if (isDamaged) {
      setShowDamage(true);
      const timer = setTimeout(() => setShowDamage(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isDamaged]);

  // Breathing animation cycle
  useEffect(() => {
    const breathInterval = setInterval(() => {
      setBreathPhase(prev => (prev + 1) % 100);
    }, 60);
    return () => clearInterval(breathInterval);
  }, []);

  // Eye glow blinking
  useEffect(() => {
    const eyeInterval = setInterval(() => {
      setEyeGlow(prev => !prev);
    }, 3000 + Math.random() * 2000);
    return () => clearInterval(eyeInterval);
  }, []);

  // Phase-based colors
  const phaseColors = {
    0: { primary: '#4a5568', glow: 'rgba(100, 120, 150, 0.7)', eyes: '#ffd700', secondary: '#63b3ed' },
    1: { primary: '#553c9a', glow: 'rgba(128, 90, 213, 0.7)', eyes: '#ff6b00', secondary: '#9f7aea' },
    2: { primary: '#1a1a2e', glow: 'rgba(50, 50, 80, 0.9)', eyes: '#ff0000', secondary: '#e53e3e' }
  };
  const colors = phaseColors[currentPhase] || phaseColors[0];

  // Calculate breathing scale
  const breathScale = 1 + Math.sin(breathPhase * 0.08) * 0.02;

  return (
    <div className="relative w-full flex justify-center mb-2 perspective-1000">
      {/* Dark Forest ambient effects */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Shadow wisps */}
        {wispPositions.map((pos, i) => (
          <div key={`wisp-${i}`} className="absolute animate-shadow-wisp"
            style={{ left: pos.left, top: pos.top, animationDelay: pos.delay, opacity: 0.4 }}
          >
            <div className="w-8 h-8 rounded-full bg-gray-800 blur-md" />
          </div>
        ))}
        
        {/* Fireflies */}
        {fireflyPositions.map((pos, i) => (
          <div key={`firefly-${i}`} className="absolute animate-firefly"
            style={{ left: pos.left, bottom: pos.bottom, animationDelay: pos.delay }}
          >
            <span className="text-xs" style={{ color: '#90EE90', filter: 'drop-shadow(0 0 6px #90EE90)' }}>•</span>
          </div>
        ))}
      </div>

      {/* Moonbeam effect */}
      <div 
        className="absolute top-0 left-1/2 w-32 h-full animate-moonbeam pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, rgba(200,200,255,0.15) 0%, transparent 60%)',
          transform: 'translateX(-50%) skewX(-15deg)'
        }}
      />

      {/* Main Owl Container */}
      <div 
        className={cn(
          "relative w-48 h-48 sm:w-56 sm:h-56 transition-all duration-300",
          showDamage && "animate-boss-shake",
          isAttacking && "animate-owl-attack"
        )}
        style={{
          transform: `scale(${breathScale})`,
        }}
      >
        {/* Owl body shadow */}
        <div 
          className="absolute inset-0 rounded-full blur-2xl animate-pulse"
          style={{
            background: colors.glow,
            transform: 'scale(1.3)',
            opacity: isAttacking ? 0.8 : 0.5
          }}
        />

        {/* Owl emoji with styling */}
        <div 
          className="relative w-full h-full flex items-center justify-center animate-owl-float"
          style={{
            filter: `drop-shadow(0 0 30px ${colors.glow}) drop-shadow(0 0 50px ${colors.secondary})`
          }}
        >
          <span 
            className={cn(
              "text-[120px] sm:text-[140px] transition-all duration-200",
              isAttacking && "brightness-125"
            )}
            style={{
              filter: `drop-shadow(0 0 20px ${colors.eyes})`
            }}
          >
            🦉
          </span>
        </div>

        {/* Glowing eyes overlay */}
        <div className="absolute top-[30%] left-[32%] pointer-events-none">
          <div 
            className={cn(
              "w-5 h-5 rounded-full transition-all duration-300",
              eyeGlow || isAttacking ? "animate-owl-eyes" : ""
            )}
            style={{
              background: `radial-gradient(circle, #fff 0%, ${colors.eyes} 50%, transparent 100%)`,
              boxShadow: `0 0 ${isAttacking ? '30px' : '15px'} ${colors.eyes}, 0 0 ${isAttacking ? '50px' : '25px'} ${colors.eyes}`
            }}
          />
        </div>
        <div className="absolute top-[30%] right-[32%] pointer-events-none">
          <div 
            className={cn(
              "w-5 h-5 rounded-full transition-all duration-300",
              eyeGlow || isAttacking ? "animate-owl-eyes" : ""
            )}
            style={{
              background: `radial-gradient(circle, #fff 0%, ${colors.eyes} 50%, transparent 100%)`,
              boxShadow: `0 0 ${isAttacking ? '30px' : '15px'} ${colors.eyes}, 0 0 ${isAttacking ? '50px' : '25px'} ${colors.eyes}`,
              animationDelay: '0.1s'
            }}
          />
        </div>

        {/* Wind effect when attacking */}
        {isAttacking && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(8)].map((_, i) => (
              <div
                key={`wind-${i}`}
                className="absolute animate-wind-gust"
                style={{
                  top: `${20 + i * 8}%`,
                  left: '-20%',
                  animationDelay: `${i * 0.1}s`
                }}
              >
                <span className="text-2xl" style={{ color: colors.secondary, opacity: 0.6 }}>〰️</span>
              </div>
            ))}
          </div>
        )}

        {/* Attack Effect Overlay */}
        {isAttacking && attackType && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <div className="relative">
              <span className="text-7xl animate-attack-burst" style={{ filter: `drop-shadow(0 0 40px ${colors.eyes})` }}>
                {OWL_ATTACK_EFFECTS[attackType]?.emoji || "💨"}
              </span>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-24 h-24 rounded-full border-4 animate-shockwave" style={{ borderColor: colors.secondary }} />
              </div>
            </div>
          </div>
        )}

        {/* Damage indicator */}
        {showDamage && (
          <div className="absolute top-0 right-0 text-4xl font-bold text-yellow-400 animate-damage-pop z-30" style={{ filter: 'drop-shadow(0 0 10px #ff0)' }}>
            💥
          </div>
        )}

        {/* Feather particles */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div
              key={`feather-${i}`}
              className="absolute animate-orbit-particle"
              style={{
                top: '50%',
                left: '50%',
                animationDelay: `${i * 0.5}s`,
                animationDuration: `${4 + (i % 2)}s`
              }}
            >
              <span className="text-sm" style={{ color: colors.secondary }}>🪶</span>
            </div>
          ))}
        </div>
      </div>

      {/* Bat particles during attack */}
      {isAttacking && attackType === 'night_terror' && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-fire-burst"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 80}%`,
                animationDelay: `${i * 0.05}s`
              }}
            >
              <span className="text-2xl">🦇</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Giant Kraken Visual Component (Level 160 Boss)
export function KrakenVisual({ currentPhase = 0, isAttacking = false, attackType = null, isDamaged = false }) {
  const [wavePhase, setWavePhase] = useState(0);
  const [tentaclePhase, setTentaclePhase] = useState(0);
  const [showDamage, setShowDamage] = useState(false);

  // Memoize bubble positions
  const bubblePositions = useMemo(() => Array.from({ length: 8 }, (_, i) => ({
    width: `${4 + (i % 3) * 2}px`, height: `${4 + (i % 3) * 2}px`,
    left: `${10 + i * 10}%`, delay: `${i * 0.5}s`, duration: `${3 + (i % 3)}s`
  })), []);

  useEffect(() => {
    if (isDamaged) {
      setShowDamage(true);
      const timer = setTimeout(() => setShowDamage(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isDamaged]);

  // Combined animation loop at reduced frequency
  useEffect(() => {
    const interval = setInterval(() => {
      setWavePhase(prev => (prev + 1) % 100);
      setTentaclePhase(prev => (prev + 2) % 360);
    }, 80);
    return () => clearInterval(interval);
  }, []);

  // Phase-based colors
  const phaseColors = {
    0: { primary: '#1e3a5f', glow: 'rgba(30, 144, 255, 0.6)', tentacles: '#9333ea', water: '#0ea5e9' },
    1: { primary: '#1e1e3f', glow: 'rgba(100, 50, 150, 0.7)', tentacles: '#7c3aed', water: '#6366f1' },
    2: { primary: '#0f0f1f', glow: 'rgba(220, 38, 38, 0.7)', tentacles: '#dc2626', water: '#831843' }
  };
  const colors = phaseColors[currentPhase] || phaseColors[0];

  // Calculate wave motion
  const waveOffset = Math.sin(wavePhase * 0.1) * 5;
  const tentacleRotation = Math.sin(tentaclePhase * 0.05) * 15;

  return (
    <div className="relative w-full flex justify-center mb-2 perspective-1000">
      {/* Ocean background effects */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Water waves */}
        {[...Array(5)].map((_, i) => (
          <div
            key={`wave-${i}`}
            className="absolute w-full h-4 opacity-30"
            style={{
              bottom: `${i * 12}%`,
              background: `linear-gradient(90deg, transparent, ${colors.water}40, transparent)`,
              animation: `waveMove ${2 + i * 0.5}s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`
            }}
          />
        ))}
        
        {/* Bubbles */}
        {bubblePositions.map((pos, i) => (
          <div
            key={`bubble-${i}`}
            className="absolute rounded-full bg-white/30 animate-float-up"
            style={{
              width: pos.width,
              height: pos.height,
              left: pos.left,
              bottom: '-10%',
              animationDelay: pos.delay,
              animationDuration: pos.duration
            }}
          />
        ))}
      </div>

      {/* Main Kraken container */}
      <div 
        className={cn(
          "relative w-48 h-48 transition-all duration-300",
          showDamage && "animate-shake",
          isAttacking && "scale-110"
        )}
        style={{ transform: `translateY(${waveOffset}px)` }}
      >
        {/* Tentacles - background */}
        {[...Array(4)].map((_, i) => (
          <div
            key={`tentacle-bg-${i}`}
            className="absolute"
            style={{
              bottom: '10%',
              left: `${15 + i * 20}%`,
              transform: `rotate(${-30 + i * 20 + Math.sin((tentaclePhase + i * 30) * 0.05) * 10}deg)`,
              transformOrigin: 'bottom center',
              zIndex: 1
            }}
          >
            <div 
              className="w-6 h-24 rounded-full"
              style={{
                background: `linear-gradient(to top, ${colors.tentacles}, ${colors.tentacles}80)`,
                boxShadow: `0 0 15px ${colors.tentacles}60`
              }}
            >
              {/* Suction cups */}
              {[...Array(4)].map((_, j) => (
                <div
                  key={j}
                  className="absolute w-3 h-2 rounded-full bg-pink-300/50"
                  style={{ 
                    top: `${20 + j * 20}%`, 
                    left: '50%', 
                    transform: 'translateX(-50%)'
                  }}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Main body - Kraken head */}
        <div 
          className="absolute top-1/4 left-1/2 -translate-x-1/2 w-32 h-32 rounded-t-full"
          style={{
            background: `linear-gradient(180deg, ${colors.tentacles} 0%, #1e1b4b 100%)`,
            boxShadow: `0 0 40px ${colors.glow}, inset 0 -20px 40px ${colors.primary}`,
            transform: `translateX(-50%) scaleY(${1 + Math.sin(wavePhase * 0.08) * 0.05})`
          }}
        >
          {/* Eyes */}
          <div className="absolute top-[35%] left-[25%] w-6 h-8 bg-yellow-300 rounded-full"
            style={{
              boxShadow: `0 0 20px ${isAttacking ? '#ff0' : '#ffd700'}`,
              animation: isAttacking ? 'pulse 0.3s infinite' : 'none'
            }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-4 bg-black rounded-full" />
          </div>
          <div className="absolute top-[35%] right-[25%] w-6 h-8 bg-yellow-300 rounded-full"
            style={{
              boxShadow: `0 0 20px ${isAttacking ? '#ff0' : '#ffd700'}`,
              animation: isAttacking ? 'pulse 0.3s infinite' : 'none'
            }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-4 bg-black rounded-full" />
          </div>

          {/* Beak */}
          <div 
            className="absolute bottom-[10%] left-1/2 -translate-x-1/2 w-0 h-0"
            style={{
              borderLeft: '12px solid transparent',
              borderRight: '12px solid transparent',
              borderTop: `20px solid ${colors.tentacles}`,
              filter: 'brightness(0.7)'
            }}
          />
        </div>

        {/* Tentacles - foreground */}
        {[...Array(4)].map((_, i) => (
          <div
            key={`tentacle-fg-${i}`}
            className="absolute"
            style={{
              bottom: '5%',
              left: `${5 + i * 25}%`,
              transform: `rotate(${-40 + i * 25 + Math.sin((tentaclePhase + i * 45) * 0.06) * 12}deg)`,
              transformOrigin: 'bottom center',
              zIndex: 10
            }}
          >
            <div 
              className="w-5 h-20 rounded-full"
              style={{
                background: `linear-gradient(to top, ${colors.tentacles}cc, ${colors.tentacles}60)`,
                boxShadow: `0 0 10px ${colors.tentacles}40`
              }}
            />
          </div>
        ))}

        {/* Attack Effect */}
        {isAttacking && attackType && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <div className="relative">
              <span className="text-7xl animate-attack-burst" style={{ filter: `drop-shadow(0 0 40px ${colors.water})` }}>
                {KRAKEN_ATTACK_EFFECTS[attackType]?.emoji || "🌊"}
              </span>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-24 h-24 rounded-full border-4 animate-shockwave" style={{ borderColor: colors.water }} />
              </div>
            </div>
          </div>
        )}

        {/* Damage indicator */}
        {showDamage && (
          <div className="absolute top-0 right-0 text-4xl font-bold text-yellow-400 animate-damage-pop z-30" style={{ filter: 'drop-shadow(0 0 10px #ff0)' }}>
            💥
          </div>
        )}

        {/* Ink particles during attack */}
        {isAttacking && attackType === 'ink_blast' && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-fire-burst"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 80}%`,
                  animationDelay: `${i * 0.05}s`
                }}
              >
                <span className="text-xl">🖤</span>
              </div>
            ))}
          </div>
        )}

        {/* Water splash during whirlpool */}
        {isAttacking && attackType === 'whirlpool' && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-spin"
                style={{
                  left: '50%',
                  top: '50%',
                  transform: `rotate(${i * 36}deg) translateY(-40px)`,
                  animationDuration: '0.5s'
                }}
              >
                <span className="text-2xl">💧</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Additional ambient effects */}
      <style>{`
        @keyframes waveMove {
          0%, 100% { transform: translateX(-10%); opacity: 0.3; }
          50% { transform: translateX(10%); opacity: 0.5; }
        }
        @keyframes float-up {
          0% { transform: translateY(0) scale(1); opacity: 0.6; }
          100% { transform: translateY(-200px) scale(0.5); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// ========== NEW BOSS VISUALS ==========

// Phoenix King Visual (Volcanic Core - Level 210, 220)
export function PhoenixVisual({ currentPhase = 0, isAttacking = false, attackType = null, isDamaged = false }) {
  const [flamePhase, setFlamePhase] = useState(0);
  const [showDamage, setShowDamage] = useState(false);
  const [wingSpread, setWingSpread] = useState(0);

  // Memoize ember positions
  const emberPositions = useMemo(() => Array.from({ length: 8 }, (_, i) => ({
    left: `${10 + i * 11}%`, delay: `${i * 0.5}s`, duration: `${2.5 + (i % 3)}s`
  })), []);

  useEffect(() => {
    if (isDamaged) {
      setShowDamage(true);
      const timer = setTimeout(() => setShowDamage(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isDamaged]);

  // Combined animation loop at reduced frequency
  useEffect(() => {
    const interval = setInterval(() => {
      setFlamePhase(prev => (prev + 1) % 100);
      setWingSpread(prev => (prev + 3) % 360);
    }, 80);
    return () => clearInterval(interval);
  }, []);

  const phaseColors = {
    0: { primary: '#ff6b00', glow: 'rgba(255, 107, 0, 0.7)', fire: '#ffaa00', secondary: '#ff4500' },
    1: { primary: '#ff0000', glow: 'rgba(255, 0, 0, 0.7)', fire: '#ff3300', secondary: '#ff6600' },
    2: { primary: '#4400ff', glow: 'rgba(68, 0, 255, 0.7)', fire: '#8800ff', secondary: '#ff00ff' }
  };
  const colors = phaseColors[currentPhase] || phaseColors[0];
  const wingRotate = Math.sin(wingSpread * 0.05) * 10;
  const breathScale = 1 + Math.sin(flamePhase * 0.1) * 0.04;

  return (
    <div className="relative w-full flex justify-center mb-2 perspective-1000">
      {/* Volcanic background effects */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {emberPositions.map((pos, i) => (
          <div
            key={`ember-${i}`}
            className="absolute animate-ember-rise"
            style={{
              left: pos.left,
              bottom: '-10px',
              animationDelay: pos.delay,
              animationDuration: pos.duration
            }}
          >
            <span className="text-xl" style={{ color: colors.fire, filter: `drop-shadow(0 0 8px ${colors.fire})` }}>🔥</span>
          </div>
        ))}
      </div>

      {/* Main Phoenix Container */}
      <div 
        className={cn(
          "relative w-48 h-48 sm:w-56 sm:h-56 transition-all duration-300",
          showDamage && "animate-boss-shake",
          isAttacking && "animate-phoenix-attack"
        )}
        style={{ transform: `scale(${breathScale}) rotate(${wingRotate * 0.2}deg)` }}
      >
        {/* Phoenix aura */}
        <div 
          className="absolute inset-0 rounded-full blur-2xl animate-pulse"
          style={{ background: `radial-gradient(circle, ${colors.glow} 0%, transparent 70%)`, transform: 'scale(1.5)' }}
        />

        {/* Phoenix body */}
        <div className="relative w-full h-full flex items-center justify-center">
          <span 
            className="text-[100px] sm:text-[120px] filter drop-shadow-2xl"
            style={{ filter: `drop-shadow(0 0 30px ${colors.fire})`, transform: `scaleX(${1 + wingRotate * 0.01})` }}
          >
            🦅
          </span>
        </div>

        {/* Fire wings effect */}
        <div className="absolute top-1/4 left-0 w-full flex justify-between px-2 pointer-events-none">
          <span className="text-4xl animate-wing-flap-left" style={{ filter: `drop-shadow(0 0 15px ${colors.fire})` }}>🔥</span>
          <span className="text-4xl animate-wing-flap-right" style={{ filter: `drop-shadow(0 0 15px ${colors.fire})` }}>🔥</span>
        </div>

        {/* Attack effect */}
        {isAttacking && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <span className="text-6xl animate-attack-burst" style={{ filter: `drop-shadow(0 0 40px ${colors.fire})` }}>
              {PHOENIX_ATTACK_EFFECTS[attackType]?.emoji || "🔥"}
            </span>
          </div>
        )}

        {showDamage && (
          <div className="absolute top-0 right-0 text-4xl font-bold text-yellow-400 animate-damage-pop z-30">💥</div>
        )}
      </div>

      <style>{`
        @keyframes ember-rise { 0% { transform: translateY(0) scale(1); opacity: 1; } 100% { transform: translateY(-200px) scale(0.3); opacity: 0; } }
        @keyframes wing-flap-left { 0%, 100% { transform: rotate(-20deg) translateX(-10px); } 50% { transform: rotate(10deg) translateX(0); } }
        @keyframes wing-flap-right { 0%, 100% { transform: rotate(20deg) translateX(10px); } 50% { transform: rotate(-10deg) translateX(0); } }
        .animate-ember-rise { animation: ember-rise 3s ease-out infinite; }
        .animate-wing-flap-left { animation: wing-flap-left 0.8s ease-in-out infinite; }
        .animate-wing-flap-right { animation: wing-flap-right 0.8s ease-in-out infinite; }
        .animate-phoenix-attack { animation: phoenix-attack 0.5s ease-in-out; }
        @keyframes phoenix-attack { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.3) translateY(-20px); } }
      `}</style>
    </div>
  );
}

// Dream Weaver Visual (Moonlight Garden - Level 230, 240)
export function DreamWeaverVisual({ currentPhase = 0, isAttacking = false, attackType = null, isDamaged = false }) {
  const [floatPhase, setFloatPhase] = useState(0);
  const [showDamage, setShowDamage] = useState(false);

  // Memoize star positions
  const starPositions = useMemo(() => Array.from({ length: 10 }, (_, i) => ({
    left: `${8 + i * 9}%`, top: `${5 + (i * 8) % 70}%`, delay: `${i * 0.3}s`,
    size: `${8 + (i % 3) * 4}px`
  })), []);

  useEffect(() => {
    if (isDamaged) {
      setShowDamage(true);
      const timer = setTimeout(() => setShowDamage(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isDamaged]);

  useEffect(() => {
    const floatInterval = setInterval(() => setFloatPhase(prev => (prev + 1) % 100), 50);
    return () => clearInterval(floatInterval);
  }, []);

  const phaseColors = {
    0: { primary: '#9f7aea', glow: 'rgba(159, 122, 234, 0.7)', stars: '#ffd700' },
    1: { primary: '#6b46c1', glow: 'rgba(107, 70, 193, 0.8)', stars: '#ff69b4' },
    2: { primary: '#322659', glow: 'rgba(50, 38, 89, 0.9)', stars: '#ff0000' }
  };
  const colors = phaseColors[currentPhase] || phaseColors[0];
  const floatY = Math.sin(floatPhase * 0.08) * 8;

  return (
    <div className="relative w-full flex justify-center mb-2 perspective-1000">
      {/* Starry night background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {starPositions.map((pos, i) => (
          <div
            key={`star-${i}`}
            className="absolute animate-twinkle"
            style={{ left: pos.left, top: pos.top, animationDelay: pos.delay }}
          >
            <span style={{ color: colors.stars, fontSize: pos.size, filter: `drop-shadow(0 0 4px ${colors.stars})` }}>✨</span>
          </div>
        ))}
      </div>

      {/* Main Dream Weaver */}
      <div 
        className={cn("relative w-48 h-48 sm:w-56 sm:h-56 transition-all duration-300", showDamage && "animate-boss-shake")}
        style={{ transform: `translateY(${floatY}px)` }}
      >
        <div className="absolute inset-0 rounded-full blur-2xl animate-pulse" style={{ background: `radial-gradient(circle, ${colors.glow} 0%, transparent 70%)`, transform: 'scale(1.4)' }} />

        <div className="relative w-full h-full flex items-center justify-center">
          <span className="text-[100px] sm:text-[120px]" style={{ filter: `drop-shadow(0 0 25px ${colors.primary})` }}>🧙</span>
        </div>

        {/* Magical orbs */}
        {[...Array(6)].map((_, i) => (
          <div
            key={`orb-${i}`}
            className="absolute animate-orbit"
            style={{
              left: '50%',
              top: '50%',
              transform: `rotate(${i * 60 + floatPhase * 2}deg) translateY(-60px)`,
              transformOrigin: 'center center'
            }}
          >
            <span className="text-2xl" style={{ filter: `drop-shadow(0 0 10px ${colors.stars})` }}>💫</span>
          </div>
        ))}

        {isAttacking && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <span className="text-6xl animate-attack-burst" style={{ filter: `drop-shadow(0 0 40px ${colors.primary})` }}>
              {DREAM_WEAVER_ATTACK_EFFECTS[attackType]?.emoji || "💫"}
            </span>
          </div>
        )}

        {showDamage && <div className="absolute top-0 right-0 text-4xl font-bold text-yellow-400 animate-damage-pop z-30">💥</div>}
      </div>

      <style>{`
        @keyframes twinkle { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.3; transform: scale(0.7); } }
        @keyframes orbit { 0% { opacity: 1; } 100% { opacity: 1; } }
        .animate-twinkle { animation: twinkle 2s ease-in-out infinite; }
        .animate-orbit { transition: transform 0.1s linear; }
      `}</style>
    </div>
  );
}

// Alchemist Visual (Alchemist's Lab - Level 250, 260)
export function AlchemistVisual({ currentPhase = 0, isAttacking = false, attackType = null, isDamaged = false }) {
  const [bubblePhase, setBubblePhase] = useState(0);
  const [showDamage, setShowDamage] = useState(false);

  useEffect(() => {
    if (isDamaged) { setShowDamage(true); const timer = setTimeout(() => setShowDamage(false), 300); return () => clearTimeout(timer); }
  }, [isDamaged]);

  useEffect(() => {
    const interval = setInterval(() => setBubblePhase(prev => (prev + 1) % 100), 60);
    return () => clearInterval(interval);
  }, []);

  const phaseColors = {
    0: { primary: '#10b981', glow: 'rgba(16, 185, 129, 0.7)', bubbles: '#22d3ee' },
    1: { primary: '#f59e0b', glow: 'rgba(245, 158, 11, 0.7)', bubbles: '#84cc16' },
    2: { primary: '#ef4444', glow: 'rgba(239, 68, 68, 0.8)', bubbles: '#a855f7' }
  };
  const colors = phaseColors[currentPhase] || phaseColors[0];

  return (
    <div className="relative w-full flex justify-center mb-2 perspective-1000">
      {/* Potion bubbles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(12)].map((_, i) => (
          <div
            key={`bubble-${i}`}
            className="absolute animate-bubble-rise"
            style={{ left: `${20 + Math.random() * 60}%`, bottom: '-20px', animationDelay: `${i * 0.4}s` }}
          >
            <span style={{ color: colors.bubbles, fontSize: `${12 + Math.random() * 12}px`, filter: `drop-shadow(0 0 5px ${colors.bubbles})` }}>🧪</span>
          </div>
        ))}
      </div>

      <div className={cn("relative w-48 h-48 sm:w-56 sm:h-56 transition-all duration-300", showDamage && "animate-boss-shake")}>
        <div className="absolute inset-0 rounded-full blur-2xl animate-pulse" style={{ background: `radial-gradient(circle, ${colors.glow} 0%, transparent 70%)`, transform: 'scale(1.4)' }} />

        <div className="relative w-full h-full flex items-center justify-center">
          <span className="text-[100px] sm:text-[120px]" style={{ filter: `drop-shadow(0 0 25px ${colors.primary})` }}>🧑‍🔬</span>
        </div>

        {/* Floating potions */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-4">
          <span className="text-3xl animate-bounce" style={{ animationDelay: '0s' }}>🧪</span>
          <span className="text-3xl animate-bounce" style={{ animationDelay: '0.2s' }}>⚗️</span>
          <span className="text-3xl animate-bounce" style={{ animationDelay: '0.4s' }}>🧫</span>
        </div>

        {isAttacking && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <span className="text-6xl animate-attack-burst" style={{ filter: `drop-shadow(0 0 40px ${colors.primary})` }}>
              {ALCHEMIST_ATTACK_EFFECTS[attackType]?.emoji || "🧪"}
            </span>
          </div>
        )}

        {showDamage && <div className="absolute top-0 right-0 text-4xl font-bold text-yellow-400 animate-damage-pop z-30">💥</div>}
      </div>

      <style>{`
        @keyframes bubble-rise { 0% { transform: translateY(0) scale(1); opacity: 1; } 100% { transform: translateY(-180px) scale(0.5); opacity: 0; } }
        .animate-bubble-rise { animation: bubble-rise 3s ease-out infinite; }
      `}</style>
    </div>
  );
}

// Ringmaster Visual (Circus Carnival - Level 270, 280)
export function RingmasterVisual({ currentPhase = 0, isAttacking = false, attackType = null, isDamaged = false }) {
  const [spinPhase, setSpinPhase] = useState(0);
  const [showDamage, setShowDamage] = useState(false);

  useEffect(() => {
    if (isDamaged) { setShowDamage(true); const timer = setTimeout(() => setShowDamage(false), 300); return () => clearTimeout(timer); }
  }, [isDamaged]);

  useEffect(() => {
    const interval = setInterval(() => setSpinPhase(prev => (prev + 1) % 360), 30);
    return () => clearInterval(interval);
  }, []);

  const phaseColors = {
    0: { primary: '#ec4899', glow: 'rgba(236, 72, 153, 0.7)', accent: '#fbbf24' },
    1: { primary: '#8b5cf6', glow: 'rgba(139, 92, 246, 0.7)', accent: '#f472b6' },
    2: { primary: '#ef4444', glow: 'rgba(239, 68, 68, 0.8)', accent: '#fcd34d' }
  };
  const colors = phaseColors[currentPhase] || phaseColors[0];

  return (
    <div className="relative w-full flex justify-center mb-2 perspective-1000">
      {/* Confetti */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <div
            key={`confetti-${i}`}
            className="absolute animate-confetti-fall"
            style={{ left: `${Math.random() * 100}%`, top: '-20px', animationDelay: `${i * 0.3}s` }}
          >
            <span style={{ fontSize: '16px' }}>{['🎪', '🎈', '🎉', '🎊', '🎭'][i % 5]}</span>
          </div>
        ))}
      </div>

      <div className={cn("relative w-48 h-48 sm:w-56 sm:h-56 transition-all duration-300", showDamage && "animate-boss-shake")}>
        <div className="absolute inset-0 rounded-full blur-2xl animate-pulse" style={{ background: `radial-gradient(circle, ${colors.glow} 0%, transparent 70%)`, transform: 'scale(1.4)' }} />

        <div className="relative w-full h-full flex items-center justify-center">
          <span className="text-[100px] sm:text-[120px]" style={{ filter: `drop-shadow(0 0 25px ${colors.primary})` }}>🎪</span>
        </div>

        {/* Juggling balls */}
        {[...Array(4)].map((_, i) => (
          <div
            key={`ball-${i}`}
            className="absolute"
            style={{
              left: '50%',
              top: '30%',
              transform: `rotate(${spinPhase + i * 90}deg) translateY(-50px)`,
              transformOrigin: 'center center'
            }}
          >
            <span className="text-2xl">🔴</span>
          </div>
        ))}

        {isAttacking && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <span className="text-6xl animate-attack-burst" style={{ filter: `drop-shadow(0 0 40px ${colors.primary})` }}>
              {CIRCUS_ATTACK_EFFECTS[attackType]?.emoji || "🎪"}
            </span>
          </div>
        )}

        {showDamage && <div className="absolute top-0 right-0 text-4xl font-bold text-yellow-400 animate-damage-pop z-30">💥</div>}
      </div>

      <style>{`
        @keyframes confetti-fall { 0% { transform: translateY(0) rotate(0deg); opacity: 1; } 100% { transform: translateY(250px) rotate(720deg); opacity: 0; } }
        .animate-confetti-fall { animation: confetti-fall 4s ease-in infinite; }
      `}</style>
    </div>
  );
}

// Pirate Captain Visual (Pirate Shipwreck - Level 290, 300)
export function PirateCaptainVisual({ currentPhase = 0, isAttacking = false, attackType = null, isDamaged = false }) {
  const [wavePhase, setWavePhase] = useState(0);
  const [showDamage, setShowDamage] = useState(false);

  useEffect(() => {
    if (isDamaged) { setShowDamage(true); const timer = setTimeout(() => setShowDamage(false), 300); return () => clearTimeout(timer); }
  }, [isDamaged]);

  useEffect(() => {
    const interval = setInterval(() => setWavePhase(prev => (prev + 1) % 100), 50);
    return () => clearInterval(interval);
  }, []);

  const phaseColors = {
    0: { primary: '#1e3a5f', glow: 'rgba(30, 58, 95, 0.7)', water: '#22d3ee' },
    1: { primary: '#064e3b', glow: 'rgba(6, 78, 59, 0.7)', water: '#14b8a6' },
    2: { primary: '#450a0a', glow: 'rgba(69, 10, 10, 0.8)', water: '#ef4444' }
  };
  const colors = phaseColors[currentPhase] || phaseColors[0];
  const sway = Math.sin(wavePhase * 0.08) * 5;

  return (
    <div className="relative w-full flex justify-center mb-2 perspective-1000">
      {/* Ocean waves */}
      <div className="absolute bottom-0 left-0 right-0 h-16 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 animate-wave" style={{ background: `linear-gradient(to top, ${colors.water}40, transparent)` }} />
      </div>

      <div 
        className={cn("relative w-48 h-48 sm:w-56 sm:h-56 transition-all duration-300", showDamage && "animate-boss-shake")}
        style={{ transform: `rotate(${sway}deg)` }}
      >
        <div className="absolute inset-0 rounded-full blur-2xl animate-pulse" style={{ background: `radial-gradient(circle, ${colors.glow} 0%, transparent 70%)`, transform: 'scale(1.4)' }} />

        <div className="relative w-full h-full flex items-center justify-center">
          <span className="text-[100px] sm:text-[120px]" style={{ filter: `drop-shadow(0 0 25px ${colors.primary})` }}>🏴‍☠️</span>
        </div>

        {/* Floating treasure */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-3">
          <span className="text-2xl animate-bounce">💰</span>
          <span className="text-2xl animate-bounce" style={{ animationDelay: '0.1s' }}>⚓</span>
          <span className="text-2xl animate-bounce" style={{ animationDelay: '0.2s' }}>🗡️</span>
        </div>

        {isAttacking && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <span className="text-6xl animate-attack-burst" style={{ filter: `drop-shadow(0 0 40px ${colors.primary})` }}>
              {PIRATE_ATTACK_EFFECTS[attackType]?.emoji || "💣"}
            </span>
          </div>
        )}

        {showDamage && <div className="absolute top-0 right-0 text-4xl font-bold text-yellow-400 animate-damage-pop z-30">💥</div>}
      </div>

      <style>{`
        @keyframes wave { 0%, 100% { transform: translateX(-5%); } 50% { transform: translateX(5%); } }
        .animate-wave { animation: wave 3s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

// Temple Guardian Visual (Cherry Blossom Temple - Level 310, 320)
export function TempleGuardianVisual({ currentPhase = 0, isAttacking = false, attackType = null, isDamaged = false }) {
  const [petalPhase, setPetalPhase] = useState(0);
  const [showDamage, setShowDamage] = useState(false);

  useEffect(() => {
    if (isDamaged) { setShowDamage(true); const timer = setTimeout(() => setShowDamage(false), 300); return () => clearTimeout(timer); }
  }, [isDamaged]);

  useEffect(() => {
    const interval = setInterval(() => setPetalPhase(prev => (prev + 1) % 100), 60);
    return () => clearInterval(interval);
  }, []);

  const phaseColors = {
    0: { primary: '#f472b6', glow: 'rgba(244, 114, 182, 0.7)', petals: '#fda4af' },
    1: { primary: '#dc2626', glow: 'rgba(220, 38, 38, 0.7)', petals: '#f87171' },
    2: { primary: '#7c2d12', glow: 'rgba(124, 45, 18, 0.8)', petals: '#991b1b' }
  };
  const colors = phaseColors[currentPhase] || phaseColors[0];

  return (
    <div className="relative w-full flex justify-center mb-2 perspective-1000">
      {/* Cherry blossom petals */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(18)].map((_, i) => (
          <div
            key={`petal-${i}`}
            className="absolute animate-petal-fall"
            style={{ left: `${Math.random() * 100}%`, top: '-20px', animationDelay: `${i * 0.4}s` }}
          >
            <span style={{ color: colors.petals, fontSize: '18px', filter: `drop-shadow(0 0 3px ${colors.petals})` }}>🌸</span>
          </div>
        ))}
      </div>

      <div className={cn("relative w-48 h-48 sm:w-56 sm:h-56 transition-all duration-300", showDamage && "animate-boss-shake")}>
        <div className="absolute inset-0 rounded-full blur-2xl animate-pulse" style={{ background: `radial-gradient(circle, ${colors.glow} 0%, transparent 70%)`, transform: 'scale(1.4)' }} />

        <div className="relative w-full h-full flex items-center justify-center">
          <span className="text-[100px] sm:text-[120px]" style={{ filter: `drop-shadow(0 0 25px ${colors.primary})` }}>⛩️</span>
        </div>

        {isAttacking && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <span className="text-6xl animate-attack-burst" style={{ filter: `drop-shadow(0 0 40px ${colors.primary})` }}>
              {TEMPLE_ATTACK_EFFECTS[attackType]?.emoji || "🌸"}
            </span>
          </div>
        )}

        {showDamage && <div className="absolute top-0 right-0 text-4xl font-bold text-yellow-400 animate-damage-pop z-30">💥</div>}
      </div>

      <style>{`
        @keyframes petal-fall { 0% { transform: translateY(0) rotate(0deg) translateX(0); opacity: 1; } 100% { transform: translateY(250px) rotate(360deg) translateX(50px); opacity: 0; } }
        .animate-petal-fall { animation: petal-fall 5s ease-in infinite; }
      `}</style>
    </div>
  );
}

// Storm King Visual (Storm Kingdom - Level 330, 340)
export function StormKingVisual({ currentPhase = 0, isAttacking = false, attackType = null, isDamaged = false }) {
  const [lightningFlash, setLightningFlash] = useState(false);
  const [showDamage, setShowDamage] = useState(false);

  useEffect(() => {
    if (isDamaged) { setShowDamage(true); const timer = setTimeout(() => setShowDamage(false), 300); return () => clearTimeout(timer); }
  }, [isDamaged]);

  useEffect(() => {
    const flashInterval = setInterval(() => {
      if (Math.random() < 0.3) {
        setLightningFlash(true);
        setTimeout(() => setLightningFlash(false), 100);
      }
    }, 1500);
    return () => clearInterval(flashInterval);
  }, []);

  const phaseColors = {
    0: { primary: '#6366f1', glow: 'rgba(99, 102, 241, 0.7)', lightning: '#fbbf24' },
    1: { primary: '#7c3aed', glow: 'rgba(124, 58, 237, 0.7)', lightning: '#fcd34d' },
    2: { primary: '#4c1d95', glow: 'rgba(76, 29, 149, 0.8)', lightning: '#fef08a' }
  };
  const colors = phaseColors[currentPhase] || phaseColors[0];

  return (
    <div className="relative w-full flex justify-center mb-2 perspective-1000">
      {/* Storm clouds */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <div
            key={`cloud-${i}`}
            className="absolute animate-cloud-drift"
            style={{ left: `${i * 18}%`, top: `${10 + (i % 3) * 15}%`, animationDelay: `${i * 0.5}s` }}
          >
            <span className="text-4xl opacity-60">🌩️</span>
          </div>
        ))}
      </div>

      {/* Lightning flash */}
      {lightningFlash && <div className="absolute inset-0 bg-yellow-200/30 z-10 pointer-events-none" />}

      <div className={cn("relative w-48 h-48 sm:w-56 sm:h-56 transition-all duration-300", showDamage && "animate-boss-shake")}>
        <div className="absolute inset-0 rounded-full blur-2xl animate-pulse" style={{ background: `radial-gradient(circle, ${colors.glow} 0%, transparent 70%)`, transform: 'scale(1.4)' }} />

        <div className="relative w-full h-full flex items-center justify-center">
          <span className="text-[100px] sm:text-[120px]" style={{ filter: `drop-shadow(0 0 25px ${colors.lightning})` }}>⚡</span>
        </div>

        {isAttacking && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <span className="text-6xl animate-attack-burst" style={{ filter: `drop-shadow(0 0 40px ${colors.lightning})` }}>
              {STORM_ATTACK_EFFECTS[attackType]?.emoji || "⚡"}
            </span>
          </div>
        )}

        {showDamage && <div className="absolute top-0 right-0 text-4xl font-bold text-yellow-400 animate-damage-pop z-30">💥</div>}
      </div>

      <style>{`
        @keyframes cloud-drift { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(20px); } }
        .animate-cloud-drift { animation: cloud-drift 8s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

// Fungal King Visual (Mushroom Cavern - Level 350, 360)
export function FungalKingVisual({ currentPhase = 0, isAttacking = false, attackType = null, isDamaged = false }) {
  const [sporePhase, setSporePhase] = useState(0);
  const [showDamage, setShowDamage] = useState(false);

  useEffect(() => {
    if (isDamaged) { setShowDamage(true); const timer = setTimeout(() => setShowDamage(false), 300); return () => clearTimeout(timer); }
  }, [isDamaged]);

  useEffect(() => {
    const interval = setInterval(() => setSporePhase(prev => (prev + 1) % 100), 70);
    return () => clearInterval(interval);
  }, []);

  const phaseColors = {
    0: { primary: '#a855f7', glow: 'rgba(168, 85, 247, 0.7)', spores: '#c084fc' },
    1: { primary: '#7e22ce', glow: 'rgba(126, 34, 206, 0.7)', spores: '#e879f9' },
    2: { primary: '#3b0764', glow: 'rgba(59, 7, 100, 0.8)', spores: '#f472b6' }
  };
  const colors = phaseColors[currentPhase] || phaseColors[0];
  const breathScale = 1 + Math.sin(sporePhase * 0.1) * 0.03;

  return (
    <div className="relative w-full flex justify-center mb-2 perspective-1000">
      {/* Floating spores */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <div
            key={`spore-${i}`}
            className="absolute animate-spore-float"
            style={{ left: `${Math.random() * 100}%`, bottom: `${Math.random() * 50}%`, animationDelay: `${i * 0.3}s` }}
          >
            <span style={{ color: colors.spores, fontSize: '12px', filter: `drop-shadow(0 0 4px ${colors.spores})` }}>●</span>
          </div>
        ))}
      </div>

      <div 
        className={cn("relative w-48 h-48 sm:w-56 sm:h-56 transition-all duration-300", showDamage && "animate-boss-shake")}
        style={{ transform: `scale(${breathScale})` }}
      >
        <div className="absolute inset-0 rounded-full blur-2xl animate-pulse" style={{ background: `radial-gradient(circle, ${colors.glow} 0%, transparent 70%)`, transform: 'scale(1.4)' }} />

        <div className="relative w-full h-full flex items-center justify-center">
          <span className="text-[100px] sm:text-[120px]" style={{ filter: `drop-shadow(0 0 25px ${colors.primary})` }}>🍄</span>
        </div>

        {isAttacking && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <span className="text-6xl animate-attack-burst" style={{ filter: `drop-shadow(0 0 40px ${colors.primary})` }}>
              {MUSHROOM_ATTACK_EFFECTS[attackType]?.emoji || "🍄"}
            </span>
          </div>
        )}

        {showDamage && <div className="absolute top-0 right-0 text-4xl font-bold text-yellow-400 animate-damage-pop z-30">💥</div>}
      </div>

      <style>{`
        @keyframes spore-float { 0%, 100% { transform: translateY(0) translateX(0); opacity: 0.7; } 50% { transform: translateY(-30px) translateX(15px); opacity: 1; } }
        .animate-spore-float { animation: spore-float 4s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

// Casino Dealer Visual (Casino Royale - Level 370, 380)
export function CasinoDealerVisual({ currentPhase = 0, isAttacking = false, attackType = null, isDamaged = false }) {
  const [cardSpin, setCardSpin] = useState(0);
  const [showDamage, setShowDamage] = useState(false);

  useEffect(() => {
    if (isDamaged) { setShowDamage(true); const timer = setTimeout(() => setShowDamage(false), 300); return () => clearTimeout(timer); }
  }, [isDamaged]);

  useEffect(() => {
    const interval = setInterval(() => setCardSpin(prev => (prev + 1) % 360), 40);
    return () => clearInterval(interval);
  }, []);

  const phaseColors = {
    0: { primary: '#dc2626', glow: 'rgba(220, 38, 38, 0.7)', accent: '#fbbf24' },
    1: { primary: '#1e1e1e', glow: 'rgba(30, 30, 30, 0.8)', accent: '#fcd34d' },
    2: { primary: '#7c2d12', glow: 'rgba(124, 45, 18, 0.8)', accent: '#f59e0b' }
  };
  const colors = phaseColors[currentPhase] || phaseColors[0];

  return (
    <div className="relative w-full flex justify-center mb-2 perspective-1000">
      {/* Flying cards */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <div
            key={`card-${i}`}
            className="absolute animate-card-flip"
            style={{ left: `${10 + i * 12}%`, top: `${20 + (i % 3) * 20}%`, animationDelay: `${i * 0.3}s` }}
          >
            <span className="text-2xl">{['🃏', '♠️', '♥️', '♦️', '♣️'][i % 5]}</span>
          </div>
        ))}
      </div>

      <div className={cn("relative w-48 h-48 sm:w-56 sm:h-56 transition-all duration-300", showDamage && "animate-boss-shake")}>
        <div className="absolute inset-0 rounded-full blur-2xl animate-pulse" style={{ background: `radial-gradient(circle, ${colors.glow} 0%, transparent 70%)`, transform: 'scale(1.4)' }} />

        <div className="relative w-full h-full flex items-center justify-center">
          <span className="text-[100px] sm:text-[120px]" style={{ filter: `drop-shadow(0 0 25px ${colors.accent})` }}>🎰</span>
        </div>

        {/* Orbiting dice */}
        {[...Array(3)].map((_, i) => (
          <div
            key={`dice-${i}`}
            className="absolute"
            style={{
              left: '50%',
              top: '50%',
              transform: `rotate(${cardSpin + i * 120}deg) translateY(-55px)`,
              transformOrigin: 'center center'
            }}
          >
            <span className="text-2xl">🎲</span>
          </div>
        ))}

        {isAttacking && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <span className="text-6xl animate-attack-burst" style={{ filter: `drop-shadow(0 0 40px ${colors.accent})` }}>
              {CASINO_ATTACK_EFFECTS[attackType]?.emoji || "🃏"}
            </span>
          </div>
        )}

        {showDamage && <div className="absolute top-0 right-0 text-4xl font-bold text-yellow-400 animate-damage-pop z-30">💥</div>}
      </div>

      <style>{`
        @keyframes card-flip { 0%, 100% { transform: rotateY(0deg) translateY(0); } 50% { transform: rotateY(180deg) translateY(-20px); } }
        .animate-card-flip { animation: card-flip 3s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

// Cosmic Entity Visual (Cosmic Nebula - Level 390, 400)
export function CosmicEntityVisual({ currentPhase = 0, isAttacking = false, attackType = null, isDamaged = false }) {
  const [voidPulse, setVoidPulse] = useState(0);
  const [showDamage, setShowDamage] = useState(false);

  useEffect(() => {
    if (isDamaged) { setShowDamage(true); const timer = setTimeout(() => setShowDamage(false), 300); return () => clearTimeout(timer); }
  }, [isDamaged]);

  useEffect(() => {
    const interval = setInterval(() => setVoidPulse(prev => (prev + 1) % 100), 50);
    return () => clearInterval(interval);
  }, []);

  const phaseColors = {
    0: { primary: '#7c3aed', glow: 'rgba(124, 58, 237, 0.7)', stars: '#e879f9' },
    1: { primary: '#4c1d95', glow: 'rgba(76, 29, 149, 0.8)', stars: '#c084fc' },
    2: { primary: '#1e1b4b', glow: 'rgba(30, 27, 75, 0.9)', stars: '#f472b6' }
  };
  const colors = phaseColors[currentPhase] || phaseColors[0];
  const pulseScale = 1 + Math.sin(voidPulse * 0.1) * 0.05;

  return (
    <div className="relative w-full flex justify-center mb-2 perspective-1000">
      {/* Cosmic particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(25)].map((_, i) => (
          <div
            key={`star-${i}`}
            className="absolute animate-cosmic-drift"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.2}s`,
              animationDuration: `${3 + Math.random() * 3}s`
            }}
          >
            <span style={{ color: colors.stars, fontSize: `${6 + Math.random() * 10}px`, filter: `drop-shadow(0 0 4px ${colors.stars})` }}>✦</span>
          </div>
        ))}
      </div>

      <div 
        className={cn("relative w-48 h-48 sm:w-56 sm:h-56 transition-all duration-300", showDamage && "animate-boss-shake")}
        style={{ transform: `scale(${pulseScale})` }}
      >
        {/* Void black hole effect */}
        <div 
          className="absolute inset-0 rounded-full"
          style={{ 
            background: `radial-gradient(circle, #000 0%, ${colors.primary}40 40%, transparent 70%)`,
            transform: `scale(${1.2 + Math.sin(voidPulse * 0.15) * 0.1})`,
            filter: 'blur(8px)'
          }}
        />
        
        <div className="absolute inset-0 rounded-full blur-2xl animate-pulse" style={{ background: `radial-gradient(circle, ${colors.glow} 0%, transparent 70%)`, transform: 'scale(1.5)' }} />

        <div className="relative w-full h-full flex items-center justify-center">
          <span className="text-[100px] sm:text-[120px]" style={{ filter: `drop-shadow(0 0 30px ${colors.primary})` }}>🌌</span>
        </div>

        {/* Orbiting celestial bodies */}
        {[...Array(5)].map((_, i) => (
          <div
            key={`orbit-${i}`}
            className="absolute"
            style={{
              left: '50%',
              top: '50%',
              transform: `rotate(${voidPulse * 2 + i * 72}deg) translateY(-${55 + i * 5}px)`,
              transformOrigin: 'center center'
            }}
          >
            <span className="text-xl">{['🌟', '💫', '⭐', '✨', '🌙'][i]}</span>
          </div>
        ))}

        {isAttacking && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <span className="text-6xl animate-attack-burst" style={{ filter: `drop-shadow(0 0 50px ${colors.primary})` }}>
              {COSMIC_ATTACK_EFFECTS[attackType]?.emoji || "🕳️"}
            </span>
          </div>
        )}

        {showDamage && <div className="absolute top-0 right-0 text-4xl font-bold text-yellow-400 animate-damage-pop z-30">💥</div>}
      </div>

      <style>{`
        @keyframes cosmic-drift { 
          0% { transform: translateY(0) translateX(0) scale(1); opacity: 0.5; } 
          50% { transform: translateY(-20px) translateX(10px) scale(1.2); opacity: 1; } 
          100% { transform: translateY(0) translateX(0) scale(1); opacity: 0.5; } 
        }
        .animate-cosmic-drift { animation: cosmic-drift 4s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

// Pharaoh Guardian Visual (Ancient Egypt - Level 200)
export function PharaohVisual({ currentPhase = 0, isAttacking = false, attackType = null, isDamaged = false }) {
  const [sandSwirl, setSandSwirl] = useState(0);
  const [showDamage, setShowDamage] = useState(false);

  // Memoize particle positions to avoid layout thrashing on re-renders
  const sandPositions = useMemo(() => Array.from({ length: 10 }, (_, i) => ({
    left: `${10 + i * 9}%`,
    delay: `${i * 0.5}s`,
    duration: `${2.5 + (i % 3)}s`,
    size: `${4 + (i % 3) * 2}px`
  })), []);

  useEffect(() => {
    if (isDamaged) { setShowDamage(true); const timer = setTimeout(() => setShowDamage(false), 300); return () => clearTimeout(timer); }
  }, [isDamaged]);

  useEffect(() => {
    const interval = setInterval(() => setSandSwirl(prev => (prev + 1) % 100), 80);
    return () => clearInterval(interval);
  }, []);

  const pulseScale = 1 + Math.sin(sandSwirl * 0.08) * 0.03;

  return (
    <div className="relative w-full flex justify-center mb-2">
      {/* Sand particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {sandPositions.map((pos, i) => (
          <div
            key={`sand-${i}`}
            className="absolute animate-sand-fall"
            style={{
              left: pos.left,
              top: `-10%`,
              animationDelay: pos.delay,
              animationDuration: pos.duration
            }}
          >
            <span style={{ color: '#d4a574', fontSize: pos.size }}>●</span>
          </div>
        ))}
      </div>

      <div 
        className={cn("relative w-48 h-48 sm:w-56 sm:h-56 transition-all duration-300", showDamage && "animate-boss-shake")}
        style={{ transform: `scale(${pulseScale})` }}
      >
        {/* Golden aura */}
        <div 
          className="absolute inset-0 rounded-full blur-2xl animate-pulse"
          style={{ 
            background: 'radial-gradient(circle, rgba(234, 179, 8, 0.6) 0%, rgba(180, 83, 9, 0.3) 50%, transparent 70%)',
            transform: 'scale(1.4)'
          }}
        />

        {/* Hieroglyph ring */}
        <div 
          className="absolute inset-2 rounded-full border-4 border-amber-500/50"
          style={{ 
            transform: `rotate(${sandSwirl * 0.5}deg)`,
            borderStyle: 'dashed'
          }}
        />

        <div className="relative w-full h-full flex items-center justify-center">
          <span className="text-[100px] sm:text-[120px]" style={{ filter: 'drop-shadow(0 0 20px #eab308)' }}>🐪</span>
        </div>

        {/* Floating ankhs */}
        {[...Array(4)].map((_, i) => (
          <div
            key={`ankh-${i}`}
            className="absolute text-2xl"
            style={{
              left: `${20 + i * 20}%`,
              top: `${10 + Math.sin(sandSwirl * 0.1 + i) * 10}%`,
              transform: `rotate(${i * 90}deg)`,
              color: '#fbbf24',
              filter: 'drop-shadow(0 0 5px #eab308)'
            }}
          >
            ☥
          </div>
        ))}

        {isAttacking && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <span className="text-6xl animate-attack-burst" style={{ filter: 'drop-shadow(0 0 30px #eab308)' }}>
              {attackType === 'tomb_collapse' ? '🏛️' : attackType === 'sand_storm' ? '🌪️' : '⚡'}
            </span>
          </div>
        )}

        {showDamage && <div className="absolute top-0 right-0 text-4xl font-bold text-yellow-400 animate-damage-pop z-30">💥</div>}
      </div>

      <style>{`
        @keyframes sand-fall { 
          0% { transform: translateY(0) rotate(0deg); opacity: 0; } 
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(300px) rotate(360deg); opacity: 0; } 
        }
        .animate-sand-fall { animation: sand-fall 3s linear infinite; }
      `}</style>
    </div>
  );
}

// Frost Giant Visual (Frozen Peaks - Level 180)
export function FrostGiantVisual({ currentPhase = 0, isAttacking = false, attackType = null, isDamaged = false }) {
  const [icePulse, setIcePulse] = useState(0);
  const [showDamage, setShowDamage] = useState(false);

  useEffect(() => {
    if (isDamaged) { setShowDamage(true); const timer = setTimeout(() => setShowDamage(false), 300); return () => clearTimeout(timer); }
  }, [isDamaged]);

  useEffect(() => {
    const interval = setInterval(() => setIcePulse(prev => (prev + 1) % 100), 50);
    return () => clearInterval(interval);
  }, []);

  const pulseScale = 1 + Math.sin(icePulse * 0.1) * 0.04;

  return (
    <div className="relative w-full flex justify-center mb-2">
      {/* Snowflakes */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <div
            key={`snow-${i}`}
            className="absolute animate-snow-fall"
            style={{
              left: `${Math.random() * 100}%`,
              top: `-5%`,
              animationDelay: `${i * 0.4}s`,
              animationDuration: `${3 + Math.random() * 2}s`
            }}
          >
            <span style={{ color: '#e0f2fe', fontSize: `${8 + Math.random() * 10}px` }}>❄</span>
          </div>
        ))}
      </div>

      <div 
        className={cn("relative w-48 h-48 sm:w-56 sm:h-56 transition-all duration-300", showDamage && "animate-boss-shake")}
        style={{ transform: `scale(${pulseScale})` }}
      >
        {/* Icy aura */}
        <div 
          className="absolute inset-0 rounded-full blur-2xl animate-pulse"
          style={{ 
            background: 'radial-gradient(circle, rgba(147, 197, 253, 0.6) 0%, rgba(59, 130, 246, 0.3) 50%, transparent 70%)',
            transform: 'scale(1.4)'
          }}
        />

        {/* Ice crystal ring */}
        <div 
          className="absolute inset-4 rounded-full"
          style={{ 
            border: '3px solid rgba(147, 197, 253, 0.5)',
            transform: `rotate(${-icePulse}deg)`
          }}
        />

        <div className="relative w-full h-full flex items-center justify-center">
          <span className="text-[100px] sm:text-[120px]" style={{ filter: 'drop-shadow(0 0 25px #3b82f6) drop-shadow(0 0 50px #93c5fd)' }}>🥶</span>
        </div>

        {/* Floating ice shards */}
        {[...Array(6)].map((_, i) => (
          <div
            key={`shard-${i}`}
            className="absolute text-xl"
            style={{
              left: '50%',
              top: '50%',
              transform: `rotate(${icePulse * 1.5 + i * 60}deg) translateY(-${60 + i * 3}px)`,
              transformOrigin: 'center center',
              color: '#bfdbfe',
              filter: 'drop-shadow(0 0 5px #60a5fa)'
            }}
          >
            💎
          </div>
        ))}

        {isAttacking && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <span className="text-6xl animate-attack-burst" style={{ filter: 'drop-shadow(0 0 30px #3b82f6)' }}>
              {attackType === 'glacier_smash' ? '🧊' : attackType === 'freeze_breath' ? '❄️' : '🌨️'}
            </span>
          </div>
        )}

        {showDamage && <div className="absolute top-0 right-0 text-4xl font-bold text-cyan-400 animate-damage-pop z-30">💥</div>}
      </div>

      <style>{`
        @keyframes snow-fall { 
          0% { transform: translateY(0) translateX(0) rotate(0deg); opacity: 0; } 
          10% { opacity: 1; }
          90% { opacity: 0.8; }
          100% { transform: translateY(250px) translateX(20px) rotate(360deg); opacity: 0; } 
        }
        .animate-snow-fall { animation: snow-fall 4s linear infinite; }
      `}</style>
    </div>
  );
}

// ========== END NEW BOSS VISUALS ==========

// Attack Warning Component - Updated to support all bosses
export function AttackWarning({ attackType, movesUntilAttack, isAttacking, bossType = 'dragon' }) {
  const attackEffects = getAttackEffects(bossType);
  const attack = attackEffects[attackType];
  const bossNames = {
    'owl': 'Owl Guardian',
    'kraken': 'Giant Kraken',
    'frost_giant': 'Frost Giant',
    'pharaoh': 'Pharaoh Guardian',
    'dragon': 'Dragon'
  };
  const bossName = bossNames[bossType] || 'Dragon';
  
  if (isAttacking) {
    return (
      <div className={cn(
        "w-full p-3 rounded-lg text-center animate-pulse border-2",
        `bg-gradient-to-r ${attack?.color || 'from-red-600 to-orange-500'}`,
        "border-white/30"
      )}>
        <div className="flex items-center justify-center gap-2 text-white font-bold">
          <span className="text-2xl">{attack?.emoji}</span>
          <span>{attack?.name}!</span>
          <span className="text-2xl">{attack?.emoji}</span>
        </div>
        <p className="text-white/80 text-sm mt-1">{attack?.description}</p>
      </div>
    );
  }
  
  if (movesUntilAttack <= 2) {
    return (
      <div className="w-full p-2 rounded-lg bg-yellow-500/20 border border-yellow-500/50 text-center">
        <div className="flex items-center justify-center gap-2 text-yellow-400">
          <Zap className="w-4 h-4 animate-pulse" />
          <span className="font-bold text-sm">
            {bossName} attacks in {movesUntilAttack} move{movesUntilAttack !== 1 ? 's' : ''}!
          </span>
          <Zap className="w-4 h-4 animate-pulse" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="w-full p-2 rounded-lg bg-gray-800/50 text-center">
      <span className="text-gray-400 text-sm">
        Next attack in <span className="text-white font-bold">{movesUntilAttack}</span> moves
      </span>
    </div>
  );
}

// Damage Popup Component
export function DamagePopup({ damage, isWeakSpot, isCombo, position }) {
  return (
    <div 
      className={cn(
        "absolute pointer-events-none font-bold text-lg animate-damage-float z-50",
        isWeakSpot ? "text-yellow-400 text-xl" : 
        isCombo ? "text-purple-400" : 
        "text-white"
      )}
      style={{
        left: position?.x || '50%',
        top: position?.y || '50%',
        transform: 'translate(-50%, -50%)'
      }}
    >
      {isWeakSpot && "⚡"}
      -{damage}
      {isCombo && " COMBO!"}
    </div>
  );
}

// Victory Modal for Boss
export function BossVictoryModal({ isOpen, rewards, onContinue }) {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-b from-yellow-900 to-red-900 rounded-2xl p-6 max-w-md w-full border-4 border-yellow-500 shadow-2xl animate-victory-appear">
        {/* Victory Header */}
        <div className="text-center mb-6">
          <div className="text-6xl mb-4 animate-bounce">🐉</div>
          <h2 className="font-display text-3xl font-bold text-yellow-400 drop-shadow-lg">
            DRAGON DEFEATED!
          </h2>
          <p className="text-orange-200 mt-2">You've conquered the Fruit Dragon!</p>
        </div>
        
        {/* Rewards */}
        <div className="space-y-3 mb-6">
          <h3 className="text-white font-bold text-center">🎁 Rewards Earned</h3>
          
          <div className="bg-black/30 rounded-lg p-3 flex items-center justify-between">
            <span className="text-white flex items-center gap-2">
              <span className="text-2xl">⭐</span> Stars
            </span>
            <span className="text-yellow-400 font-bold text-xl">+{rewards?.stars || 100}</span>
          </div>
          
          <div className="bg-black/30 rounded-lg p-3 flex items-center justify-between">
            <span className="text-white flex items-center gap-2">
              <span className="text-2xl">✨</span> XP Bonus
            </span>
            <span className="text-purple-400 font-bold text-xl">+{rewards?.xp || 5000}</span>
          </div>
          
          {rewards?.frame && (
            <div className="bg-gradient-to-r from-yellow-600/30 to-orange-600/30 rounded-lg p-3 border border-yellow-500/50">
              <div className="flex items-center justify-between">
                <span className="text-white flex items-center gap-2">
                  <span className="text-2xl">🖼️</span> Exclusive Frame
                </span>
                <span className="text-yellow-400 font-bold">{rewards?.frameName}</span>
              </div>
              <p className="text-orange-200 text-xs mt-1">A rare reward for dragon slayers!</p>
            </div>
          )}
        </div>
        
        {/* Continue Button */}
        <button
          onClick={onContinue}
          className="w-full py-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-white font-display font-bold text-xl rounded-xl shadow-lg transition-all hover:scale-105 border-2 border-yellow-400"
        >
          🎉 Continue 🎉
        </button>
      </div>
    </div>
  );
}

// Calculate boss HP based on level number
// Formula: BASE_HP + (level * HP_PER_LEVEL) with scaling tiers
const calculateBossHP = (level) => {
  const BASE_HP = 3000;
  const HP_PER_LEVEL = 50;
  
  // Apply tier multipliers for higher level bosses
  let tierMultiplier = 1.0;
  if (level >= 300) tierMultiplier = 1.5;
  else if (level >= 200) tierMultiplier = 1.3;
  else if (level >= 150) tierMultiplier = 1.15;
  
  const scaledHP = Math.round((BASE_HP + (level * HP_PER_LEVEL)) * tierMultiplier);
  console.log(`[Boss HP] Level ${level}: Base=${BASE_HP}, PerLevel=${HP_PER_LEVEL}, Tier=${tierMultiplier}x, Final=${scaledHP}`);
  return scaledHP;
};

// Custom hook for boss fight logic
// level parameter is used to calculate HP dynamically
export function useBossFight(bossConfig, shieldActive = false, onShieldUsed = null, level = 100) {
  // Calculate HP based on level, not from config
  const calculatedHP = calculateBossHP(level);
  
  const [currentPhase, setCurrentPhase] = useState(0);
  const [currentHP, setCurrentHP] = useState(calculatedHP);
  const [maxHP, setMaxHP] = useState(calculatedHP);
  const [movesSinceAttack, setMovesSinceAttack] = useState(0);
  const [isAttacking, setIsAttacking] = useState(false);
  const [attackBlocked, setAttackBlocked] = useState(false);
  const [isDamaged, setIsDamaged] = useState(false);
  const [totalDamageDealt, setTotalDamageDealt] = useState(0);
  const [isDefeated, setIsDefeated] = useState(false);
  
  // Reset state when bossConfig or level changes (level restart)
  useEffect(() => {
    if (bossConfig?.phases?.[0]) {
      const newHP = calculateBossHP(level);
      setCurrentPhase(0);
      setCurrentHP(newHP);
      setMaxHP(newHP);
      setMovesSinceAttack(0);
      setIsAttacking(false);
      setAttackBlocked(false);
      setIsDamaged(false);
      setTotalDamageDealt(0);
      setIsDefeated(false);
    }
  }, [bossConfig, level]);
  
  const phase = bossConfig?.phases?.[currentPhase];
  const attackInterval = bossConfig?.attackInterval || 5;
  
  // Calculate damage from matched fruits
  const calculateDamage = useCallback((matchedFruits, comboLevel = 1) => {
    if (!bossConfig || !phase) return 0;
    
    let totalDamage = 0;
    const baseDamage = bossConfig.baseDamage || 10;
    const weakSpotMultiplier = bossConfig.weakSpotMultiplier || 2.5;
    const comboMultiplier = bossConfig.comboMultiplier || 1.5;
    
    // Count fruits and check for weak spot
    let weakSpotCount = 0;
    let normalCount = 0;
    
    matchedFruits.forEach(fruit => {
      if (fruit === phase.weakSpot) {
        weakSpotCount++;
      } else {
        normalCount++;
      }
    });
    
    // Calculate base damage
    const normalDamage = normalCount * baseDamage;
    const weakSpotDamage = weakSpotCount * baseDamage * weakSpotMultiplier;
    const rawDamage = normalDamage + weakSpotDamage;
    
    // Apply combo bonus - ADDITIVE not exponential, capped at 3x max
    // Each combo level adds 20% bonus, max 200% bonus (3x total)
    const comboBonus = Math.min(2.0, (comboLevel - 1) * 0.2);
    totalDamage = rawDamage * (1 + comboBonus);
    
    return Math.round(totalDamage);
  }, [bossConfig, phase]);
  
  // Deal damage to boss
  const dealDamage = useCallback((damage) => {
    if (isDefeated) return;
    
    setIsDamaged(true);
    setTimeout(() => setIsDamaged(false), 300);
    
    setCurrentHP(prev => {
      const newHP = Math.max(0, prev - damage);
      
      // Boss defeated when HP reaches 0 (single phase system)
      if (newHP <= 0) {
        setIsDefeated(true);
        return 0;
      }
      
      return newHP;
    });
    
    setTotalDamageDealt(prev => prev + damage);
  }, [isDefeated, currentHP]);
  
  // Track moves and trigger attacks
  const onMove = useCallback(() => {
    setMovesSinceAttack(prev => {
      const newCount = prev + 1;
      if (newCount >= attackInterval) {
        // Check if Guardian Shield is active
        if (shieldActive) {
          // Block the attack!
          setAttackBlocked(true);
          setTimeout(() => setAttackBlocked(false), 1500);
          // Notify parent to deactivate shield
          if (onShieldUsed) {
            onShieldUsed();
          }
          return 0; // Reset counter
        }
        // Trigger boss attack
        setIsAttacking(true);
        setTimeout(() => setIsAttacking(false), 1500);
        return 0;
      }
      return newCount;
    });
  }, [attackInterval, shieldActive, onShieldUsed]);
  
  // Get cells to be affected by dragon attack
  const getAttackCells = useCallback((boardSize) => {
    if (!phase) return [];
    
    const attack = ATTACK_EFFECTS[phase.attack];
    const cells = [];
    const count = attack?.cellsAffected || 3;
    
    if (phase.attack === 'lava_rain') {
      // Full row
      const row = Math.floor(Math.random() * boardSize);
      for (let col = 0; col < boardSize; col++) {
        cells.push({ row, col });
      }
    } else {
      // Random cells
      while (cells.length < count) {
        const row = Math.floor(Math.random() * boardSize);
        const col = Math.floor(Math.random() * boardSize);
        if (!cells.some(c => c.row === row && c.col === col)) {
          cells.push({ row, col });
        }
      }
    }
    
    return cells;
  }, [phase]);
  
  const movesUntilAttack = attackInterval - movesSinceAttack;
  
  return {
    currentPhase,
    currentHP,
    maxHP,
    phase,
    isAttacking,
    attackBlocked,
    isDamaged,
    isDefeated,
    movesUntilAttack,
    totalDamageDealt,
    calculateDamage,
    dealDamage,
    onMove,
    getAttackCells,
    shouldShuffle: isAttacking && phase?.attack === 'inferno'
  };
}
